/**
 * GDELT investigation collector.
 *
 * Queries the GDELT DOC 2.0 API by keyword + date + geographic filter.
 * GDELT is free and requires no API key — always available as a fallback.
 *
 * Returns article text content suitable for entity extraction.
 */

import { getApiBase } from '@/app/api-router';
import type { CollectionTask } from '../types';

// ---------------------------------------------------------------------------
// GDELT DOC 2.0 API
// ---------------------------------------------------------------------------

const GDELT_DOC_API = 'https://api.gdeltproject.org/api/v2/doc/doc';

/** Default timeout for GDELT queries (ms). */
const DEFAULT_TIMEOUT_MS = 30_000;

/** Maximum articles to fetch per query. */
const MAX_RECORDS = 50;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GDELTArticle {
  url: string;
  title: string;
  seendate: string;
  domain: string;
  language: string;
  sourcecountry: string;
}

export interface GDELTCollectionResult {
  articles: GDELTArticle[];
  rawText: string;
  articleCount: number;
  query: string;
}

// ---------------------------------------------------------------------------
// Query builder
// ---------------------------------------------------------------------------

/**
 * Build a GDELT DOC 2.0 query URL from a collection task.
 * Uses artlist mode to get individual article metadata.
 */
function buildGDELTUrl(task: CollectionTask): string {
  const query = task.query as Record<string, unknown>;
  const keywords = (query.keywords as string[]) ?? [];
  const maxRecords = (query.maxRecords as number) ?? MAX_RECORDS;

  // Build keyword query — wrap multi-word phrases in quotes
  const keywordQuery = keywords
    .filter(k => k.length > 0)
    .map(k => (k.includes(' ') ? `"${k}"` : k))
    .join(' OR ');

  if (!keywordQuery) {
    throw new Error('GDELT query requires at least one keyword');
  }

  // Temporal bounds
  const temporal = query.temporalBounds as { start?: string; end?: string } | undefined;
  let startDate = '';
  let endDate = '';

  if (temporal?.start) {
    startDate = temporal.start.replace(/[-T:Z]/g, '').slice(0, 14);
  }
  if (temporal?.end) {
    endDate = temporal.end.replace(/[-T:Z]/g, '').slice(0, 14);
  }

  // Build URL
  const params = new URLSearchParams({
    query: keywordQuery,
    mode: 'artlist',
    maxrecords: String(Math.min(maxRecords, MAX_RECORDS)),
    format: 'json',
    sort: 'DateDesc',
  });

  if (startDate) {
    params.set('startdatetime', startDate);
  }
  if (endDate) {
    params.set('enddatetime', endDate);
  }

  // Geographic filter: GDELT DOC API doesn't support precise bbox queries.
  // Geographic bounds are enforced later by the deterministic validation
  // step (validation.ts plausibility checks) on extracted entities.

  return `${GDELT_DOC_API}?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Collector
// ---------------------------------------------------------------------------

/**
 * Fetch articles from GDELT for a collection task.
 * Returns concatenated article titles and metadata as raw text
 * suitable for entity extraction.
 */
export async function collectFromGDELT(
  task: CollectionTask,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<GDELTCollectionResult> {
  const url = buildGDELTUrl(task);

  const response = await globalThis.fetch(url, {
    headers: {
      'User-Agent': 'Sentinel/1.0 OSINT Platform',
      Accept: 'application/json',
    },
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!response.ok) {
    throw new Error(`GDELT API returned ${response.status}: ${response.statusText}`);
  }

  const data = await response.json() as { articles?: GDELTArticle[] };
  const articles = data.articles ?? [];

  // Build raw text from articles for entity extraction.
  // Each article contributes its title, domain, and date.
  // The entity extractor will process this text to find entities.
  const textParts: string[] = [];

  for (const article of articles) {
    const parts: string[] = [];

    if (article.title) {
      parts.push(article.title);
    }
    if (article.domain) {
      parts.push(`Source: ${article.domain}`);
    }
    if (article.seendate) {
      parts.push(`Date: ${article.seendate}`);
    }
    if (article.url) {
      parts.push(`URL: ${article.url}`);
    }

    if (parts.length > 0) {
      textParts.push(parts.join('\n'));
    }
  }

  const rawText = textParts.join('\n\n---\n\n');
  const queryParam = task.query as Record<string, unknown>;
  const keywords = (queryParam.keywords as string[]) ?? [];

  return {
    articles,
    rawText: rawText || 'No articles found matching the query.',
    articleCount: articles.length,
    query: keywords.join(', '),
  };
}

/**
 * Fetch full article content from a URL via the RSS proxy.
 * Falls back to title-only if content fetch fails.
 * This is a best-effort enrichment — not all URLs are accessible.
 */
export async function fetchArticleContent(
  articleUrl: string,
  timeoutMs: number = 10_000,
): Promise<string | null> {
  try {
    // Use the RSS proxy to fetch article content (respects allowlist)
    const proxyUrl = `${getApiBase()}/api/rss/feed?url=${encodeURIComponent(articleUrl)}`;

    const response = await globalThis.fetch(proxyUrl, {
      signal: AbortSignal.timeout(timeoutMs),
    });

    if (!response.ok) return null;

    const text = await response.text();
    // Strip HTML tags for raw text extraction
    return text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, 5000);
  } catch {
    return null;
  }
}
